/**
 * AIImageModal - Modal Gerar Imagem com IA - extraído VERBATIM de ProductsContent.js (Fase 2 refactor).
 * Recebe estado/handlers via props do closure de ProductsContent.
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const AIImageModal = ({
  showAIImageModal,
  setShowAIImageModal,
  aiImageConfig,
  setAiImageConfig,
  aiImageLoading,
  aiImageResult,
  generateAIImage,
  applyAIImage,
  formData,
  categories,
  loading,
}) => {
  return (
      <Dialog open={showAIImageModal} onOpenChange={setShowAIImageModal}>
        <DialogContent
          data-testid="ai-image-modal"
          closeButtonTestId="ai-image-modal-close-button"
          className="glass-modal max-w-lg max-h-[95vh] flex flex-col p-0 overflow-hidden"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="p-4 pb-2 border-b border-gray-700/50 flex-shrink-0">
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <Sparkles size={18} className="text-purple-400" />
              Gerar Imagem com IA
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1">{formData.name || 'Produto'} • {categories.find(c => c.id === formData.category_id)?.name || 'Sem categoria'}</p>
          </DialogHeader>
          
          {/* Área com scroll */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Campo Chave Emergent */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                🔑 Chave Emergent Universal
              </label>
              <input
                type="password"
                className="glass-input w-full text-sm py-2 px-3"
                value={aiImageConfig.emergent_key}
                onChange={(e) => setAiImageConfig({...aiImageConfig, emergent_key: e.target.value})}
                placeholder="sk-emergent-..."
                data-testid="ai-image-key-input"
              />
            </div>

            {/* Estilo e Ambiente em grid */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-400 mb-1">📷 Estilo</label>
                <select
                  className="glass-input w-full text-xs py-2 px-2"
                  value={aiImageConfig.photo_style}
                  onChange={(e) => setAiImageConfig({...aiImageConfig, photo_style: e.target.value})}
                  data-testid="ai-image-style-select"
                >
                  <option value="amateur">📱 Caseira</option>
                  <option value="delivery">📦 Delivery</option>
                  <option value="semi_pro">📸 Semi-pro</option>
                  <option value="professional">🎬 Pro</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">🇧🇷 Ambiente Brasileiro</label>
                <select
                  className="glass-input w-full text-xs py-2 px-2"
                  value={aiImageConfig.environment}
                  onChange={(e) => setAiImageConfig({...aiImageConfig, environment: e.target.value})}
                  data-testid="ai-image-environment-select"
                >
                  <option value="auto">🎯 Auto-detectar</option>
                  <optgroup label="🇧🇷 Ambientes Brasileiros">
                    <option value="mesa_casa_br">🏠 Mesa de casa BR</option>
                    <option value="balcao_lanchonete">🍔 Lanchonete BR</option>
                    <option value="self_service">🍛 Self-service/Quilo</option>
                    <option value="marmitex">📦 Marmitex</option>
                    <option value="churrascaria">🥩 Churrascaria</option>
                    <option value="feira_rua">🛒 Feira/Rua</option>
                    <option value="acaiteria">🍇 Açaiteria</option>
                    <option value="boteco">🍺 Boteco</option>
                    <option value="pizzaria_br">🍕 Pizzaria BR</option>
                    <option value="padaria_br">🥐 Padaria BR</option>
                    <option value="pastelaria">🥟 Pastelaria</option>
                  </optgroup>
                  <optgroup label="⬜ Neutros">
                    <option value="prato_simples">🍽️ Prato simples</option>
                    <option value="tabua_madeira">🪵 Tábua de madeira</option>
                    <option value="embalagem_delivery">📦 Embalagem delivery</option>
                    <option value="fundo_neutro">⬜ Fundo neutro</option>
                  </optgroup>
                </select>
              </div>
            </div>

            {/* Detalhes Extras */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">💬 Detalhes extras</label>
              <input
                type="text"
                className="glass-input w-full text-sm py-2 px-3"
                value={aiImageConfig.extra_details}
                onChange={(e) => setAiImageConfig({...aiImageConfig, extra_details: e.target.value})}
                placeholder="Ex: queijo derretendo, bacon crocante..."
                data-testid="ai-image-details-input"
              />
            </div>

            {/* Usar logomarca da loja */}
            <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer bg-slate-800/40 border border-white/10 rounded-lg p-2.5">
              <input
                type="checkbox"
                checked={aiImageConfig.use_logo}
                onChange={(e) => setAiImageConfig({...aiImageConfig, use_logo: e.target.checked})}
                className="rounded border-gray-600 w-4 h-4 mt-0.5"
                data-testid="ai-image-use-logo-checkbox"
              />
              <span>🏷️ Usar minha logomarca na imagem
                <span className="block text-[11px] text-gray-500">A IA estampa a logo da loja na embalagem/cenário. Cadastre a logo em Configurações.</span>
              </span>
            </label>

            {/* Lembrar Configurações */}
            <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                data-testid="ai-image-remember-key-checkbox"
                checked={aiImageConfig.remember_config}
                onChange={(e) => setAiImageConfig({...aiImageConfig, remember_config: e.target.checked})}
                className="rounded border-gray-600 w-4 h-4"
              />
              Lembrar chave
            </label>

            {/* Resultado - Preview da imagem */}
            {aiImageResult?.success && (
              <div className="border border-green-500/30 rounded-lg overflow-hidden bg-green-900/10">
                <img 
                  src={aiImageResult.image_url} 
                  alt="Imagem gerada" 
                  className="w-full h-40 object-cover"
                />
                <p className="text-xs text-green-400 text-center py-2">✓ Imagem gerada com sucesso</p>
              </div>
            )}

            {/* Erro */}
            {aiImageResult && !aiImageResult.success && (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-400 text-sm">❌ {aiImageResult.error}</p>
              </div>
            )}
          </div>

          {/* Botões fixos na parte inferior */}
          <div className="p-4 pt-2 border-t border-gray-700/50 flex-shrink-0 bg-gray-900/80">
            {aiImageResult?.success ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="regenerate-ai-image-button"
                  onClick={generateAIImage}
                  disabled={aiImageLoading}
                  className="flex-1 glass-button py-3 text-sm"
                >
                  🔄 Gerar Outra
                </button>
                <button
                  type="button"
                  onClick={applyAIImage}
                  className="flex-1 glass-button-primary py-3 text-sm font-medium"
                  data-testid="apply-ai-image-button"
                >
                  ✅ Usar Esta
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="cancel-ai-image-button"
                  onClick={() => setShowAIImageModal(false)}
                  className="flex-1 glass-button py-3 text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={generateAIImage}
                  disabled={aiImageLoading || !aiImageConfig.emergent_key || !formData.name}
                  className="flex-1 glass-button-primary py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="generate-ai-image-button"
                >
                  {aiImageLoading ? (
                    <>
                      <div className="loading-spinner" style={{width: '16px', height: '16px'}}></div>
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Gerar
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

  );
};

export default AIImageModal;
